import { LayoutDashboard, Package, FolderTree, ShoppingCart, Settings, LogOut, CreditCard } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '../config/api.config';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { authStore, authService } from '../auth';

export function Sidebar() {
  const userInfo = authStore.getUserInfo();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { id: 'products', icon: Package, label: 'Products', path: '/products' },
    { id: 'categories', icon: FolderTree, label: 'Categories', path: '/categories' },
    { id: 'orders', icon: ShoppingCart, label: 'Orders', path: '/orders' },
    { id: 'payment-gateway', icon: CreditCard, label: 'Payment Gateway', path: '/payment-gateway' },
    { id: 'settings', icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const displayName = userInfo?.name || userInfo?.preferred_username || 'Admin User';
  const displayEmail = userInfo?.email || `admin@${APP_CONFIG.COMPANY_NAME.toLowerCase().replace(/\s+/g, '')}.com`;

  const initials = (displayName || APP_CONFIG.COMPANY_NAME)
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <aside className="w-64 border-r border-border flex flex-col bg-background">
      <div className="p-6 border-b border-border cursor-pointer flex items-center gap-3" onClick={() => navigate('/')}>
        {APP_CONFIG.LOGO_URL && (
          <img src={APP_CONFIG.LOGO_URL} alt={APP_CONFIG.COMPANY_NAME} className="h-8 w-8 object-contain shrink-0" />
        )}
        <div className="overflow-hidden">
          <h1 className="font-semibold text-lg truncate leading-tight">{APP_CONFIG.COMPANY_NAME}</h1>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Admin Panel</p>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.id === 'dashboard' && location.pathname === '/');

            return (
              <li key={item.id}>
                <Button
                  variant={isActive ? 'secondary' : 'ghost'}
                  className={`w-full justify-start gap-3 ${isActive ? 'bg-secondary' : ''}`}
                  onClick={() => navigate(item.path)}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="p-4 border-t border-border flex flex-col gap-2">
        <div className="flex items-center gap-3 px-4 py-3">
          <Avatar>
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="overflow-hidden flex-1">
            <p className="font-medium text-sm truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{displayEmail}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          onClick={() => authService.logout()}
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </Button>
      </div>
    </aside>
  );
}
